import React from 'react';
import { Card, CardContent, Typography, Box, useTheme } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useBalanceOverview } from '../../hooks/useBalanceOverview';

// No props needed, always shows past 6 months
const BalanceOverviewChart: React.FC = () => {
  const theme = useTheme();
  const { data, isLoading, isError } = useBalanceOverview();

  return (
    <Card sx={{ height: '100%', backdropFilter: 'blur(20px)', backgroundColor: 'rgba(255, 255, 255, 0.9)', boxShadow: '0px 2px 24px rgba(0, 0, 0, 0.1)', borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
          Balance Overview
        </Typography>
        <Box sx={{ height: 400 }}>
          {isLoading ? (
            <Typography>Loading...</Typography>
          ) : isError ? (
            <Typography color="error">Failed to load balance overview.</Typography>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.2}/>
                    <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="date" stroke={theme.palette.text.secondary} tickFormatter={d => {
                  const date = new Date(d);
                  return date.toLocaleString('default', { month: 'short', year: '2-digit' });
                }} />
                <YAxis stroke={theme.palette.text.secondary} tickFormatter={v => `$${v.toLocaleString()}`} />
                <Tooltip
                  formatter={(value: number) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  labelFormatter={label => {
                    const date = new Date(label);
                    return `Month: ${date.toLocaleString('default', { month: 'long', year: 'numeric' })}`;
                  }}
                  contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', borderRadius: 8, border: `1px solid ${theme.palette.divider}`, boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)' }}
                />
                <Area
                  type="monotone"
                  dataKey="totalBalance"
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#balanceGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default BalanceOverviewChart; 